import OpenAI, { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const emailDeliveryAnalysisService = {
  /**
   * Analyze email delivery failures and generate insights
   */
  async analyzeDeliveryFailures(failedEmails) {
    try {
      if (!failedEmails || failedEmails?.length === 0) {
        return {
          patterns: [],
          rootCauses: [],
          recommendations: [],
          summary: 'No failed emails to analyze'
        };
      }

      const failureData = failedEmails?.map(email => ({
        recipient: email?.recipient_email,
        errorMessage: email?.error_message || email?.notification_delivery_log?.[0]?.error_details || 'Unknown error',
        timestamp: email?.created_at,
        retryCount: email?.retry_count || 0,
        deliveryChannel: email?.notification_delivery_log?.[0]?.delivery_channel || 'email'
      }));

      const prompt = `You are an email deliverability expert. Analyze the following failed invoice email deliveries and identify patterns, root causes, and provide actionable recommendations.

Failed Email Data:
${JSON.stringify(failureData, null, 2)}

Provide:
1. Common failure patterns (group similar errors)
2. Root causes for each pattern
3. Specific corrective actions to improve deliverability
4. Priority level for each recommendation (high/medium/low)
5. Overall summary of the delivery issues`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are an expert in email deliverability, SMTP protocols, and email infrastructure. Provide detailed, technical analysis with actionable solutions.' 
          },
          { role: 'user', content: prompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'delivery_failure_analysis',
            schema: {
              type: 'object',
              properties: {
                patterns: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      patternName: { type: 'string' },
                      frequency: { type: 'number' },
                      affectedEmails: { type: 'number' },
                      description: { type: 'string' }
                    },
                    required: ['patternName', 'frequency', 'description']
                  }
                },
                rootCauses: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      cause: { type: 'string' },
                      relatedPattern: { type: 'string' },
                      technicalDetails: { type: 'string' }
                    },
                    required: ['cause', 'relatedPattern']
                  }
                },
                recommendations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      action: { type: 'string' },
                      priority: { type: 'string' },
                      expectedImpact: { type: 'string' },
                      implementationSteps: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['action', 'priority', 'expectedImpact']
                  }
                },
                summary: { type: 'string' }
              },
              required: ['patterns', 'rootCauses', 'recommendations', 'summary'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',
        verbosity: 'high'
      });

      const analysis = JSON.parse(response?.choices?.[0]?.message?.content);
      
      return {
        ...analysis,
        analyzedAt: new Date()?.toISOString(),
        emailCount: failedEmails?.length
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing delivery failures:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Generate corrective actions for a specific failed email
   */
  async generateCorrectiveActions(emailData) {
    try {
      const errorDetails = {
        recipient: emailData?.recipient_email,
        errorMessage: emailData?.error_message || emailData?.notification_delivery_log?.[0]?.error_details,
        retryCount: emailData?.retry_count || 0,
        subject: emailData?.subject || 'Invoice Request',
        timestamp: emailData?.created_at
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are an email deliverability troubleshooting expert. Generate specific, actionable corrective steps for email delivery failures.' 
          },
          { 
            role: 'user', 
            content: `Generate corrective actions for this failed email delivery:\n\nError Details:\n${JSON.stringify(errorDetails, null, 2)}\n\nProvide immediate actions, technical fixes, and preventive measures.` 
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'corrective_actions',
            schema: {
              type: 'object',
              properties: {
                immediateActions: {
                  type: 'array',
                  items: { type: 'string' },
                  description: 'Actions to take right now'
                },
                technicalFixes: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      fix: { type: 'string' },
                      implementation: { type: 'string' }
                    },
                    required: ['fix', 'implementation']
                  }
                },
                preventiveMeasures: {
                  type: 'array',
                  items: { type: 'string' }
                },
                estimatedResolutionTime: { type: 'string' }
              },
              required: ['immediateActions', 'technicalFixes', 'preventiveMeasures'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'medium',
        verbosity: 'medium'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating corrective actions:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Suggest content improvements for invoice emails to improve deliverability
   */
  async suggestContentFixes(emailContent, failureContext = {}) {
    try {
      const prompt = `Analyze this invoice email content and suggest improvements to enhance deliverability and avoid spam filters.

Email Content:
Subject: ${emailContent?.subject || 'Invoice Request'}
Body: ${emailContent?.body || 'Standard invoice request email'}

Failure Context:
${JSON.stringify(failureContext, null, 2)}

Provide specific content improvements focusing on:
1. Subject line optimization
2. Email body structure and formatting
3. Spam trigger word removal
4. Professional tone and clarity
5. Call-to-action improvements`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are an email marketing and deliverability expert. Suggest content improvements that enhance deliverability while maintaining professional communication.' 
          },
          { role: 'user', content: prompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'content_suggestions',
            schema: {
              type: 'object',
              properties: {
                subjectLineImprovements: {
                  type: 'object',
                  properties: {
                    original: { type: 'string' },
                    suggested: { type: 'string' },
                    reasoning: { type: 'string' }
                  },
                  required: ['suggested', 'reasoning']
                },
                bodyImprovements: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      issue: { type: 'string' },
                      suggestion: { type: 'string' },
                      impact: { type: 'string' }
                    },
                    required: ['issue', 'suggestion', 'impact']
                  }
                },
                spamTriggers: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      trigger: { type: 'string' },
                      replacement: { type: 'string' }
                    },
                    required: ['trigger', 'replacement']
                  }
                },
                overallScore: {
                  type: 'object',
                  properties: {
                    before: { type: 'number' },
                    after: { type: 'number' },
                    improvement: { type: 'string' }
                  },
                  required: ['before', 'after', 'improvement']
                }
              },
              required: ['subjectLineImprovements', 'bodyImprovements', 'spamTriggers', 'overallScore'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'medium',
        verbosity: 'medium'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error suggesting content fixes:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Get comprehensive email deliverability report
   */
  async getDeliverabilityReport() {
    try {
      const { data: allEmails, error } = await supabase
        ?.from('notification_queue')
        ?.select(`
          *,
          notification_delivery_log(
            id,
            delivery_channel,
            delivery_status,
            delivered_at,
            error_details
          )
        `)
        ?.eq('notification_type', 'compliance_alert')
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) throw error;

      const failedEmails = allEmails?.filter(e => e?.notification_status === 'failed') || [];
      
      if (failedEmails?.length === 0) {
        return {
          hasFailures: false,
          message: 'No delivery failures detected. Email system is performing well.',
          successRate: allEmails?.length > 0 ? ((allEmails?.filter(e => e?.notification_status === 'delivered')?.length / allEmails?.length) * 100)?.toFixed(1) : 100
        };
      }

      const analysis = await this.analyzeDeliveryFailures(failedEmails);
      
      return {
        hasFailures: true,
        totalEmails: allEmails?.length,
        failedCount: failedEmails?.length,
        successRate: ((allEmails?.filter(e => e?.notification_status === 'delivered')?.length / allEmails?.length) * 100)?.toFixed(1),
        analysis,
        generatedAt: new Date()?.toISOString()
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating deliverability report:', error);
      }
      throw new Error(errorInfo.message);
    }
  }
};

export default emailDeliveryAnalysisService;
